import React, { useEffect, useMemo, useState } from "react";
import SearchButton from "../SearchButton/SearchButton";
import Table from "../Table2";
import { observer } from "mobx-react";
import EmptyLiabilities from "../EmptyLiabilities/EmptyLiabilities";
import {
  commaSeperated,
  getDisplayDate,
  getLimitPer,
  numWithUnit,
} from "../../utils/functions";

import UserStore from "../../store/UserStore";
import Modal from "../Modal";
import TransactionModal from "../TransactionModal/TransactionModal";
import PerformanceStore from "../../store/PerformanceStore";
import OverflowingText from "../OverflowingText/OverflowingText";
import { useParams } from "react-router";
import { noSearchResult } from "../../utils/constants";
import SearchResult from "../SearchButton/SearchResult";

const UnrealisedGainLossRenderer = ({ data }) => {
  const amountData = numWithUnit(data.datUnrealGain, data.dataUnrelUnit);
  const percentageData = data?.unrealised_gain?.subtext;
  const displayPercentageData = getLimitPer(data?.unrealised_gain?.subtext);
  const isNegative = Number(percentageData) < 0;

  return (
    <p className="instrument_name_cell font-roboto">
      <span className="sr-only">Unrealised Gain</span>
      {amountData}
      &nbsp;&nbsp;
      {!!percentageData ? (
        isNegative ? (
          <>
            <span className="sr-only">Decreased By</span>
            <span className="unrealPerc negative">
              {displayPercentageData}%
            </span>
          </>
        ) : (
          <>
            <span className="sr-only">Increased By</span>
            <span
              aria-label={`Increased By ${displayPercentageData}`}
              className="unrealPerc"
            >
              {displayPercentageData}%
            </span>
          </>
        )
      ) : (
        "-"
      )}
    </p>
  );
};

const QuickDetailsHead = observer(
  ({ dispImg, instrumentName, instrumentDetails }) => {
    const { dataId } = useParams();
    const dispTitle = {
      2: "Equity",
      3: "Mutual Fund",
      4: "Fixed Income",
      5: "Alternatives",
    };
    const [openModal, setOpenModal] = useState(false);
    const [search, setSearch] = useState("");
    const [lastTableAction, setLastTableAction] = useState("");
    const [searchMessage, setSearchMessage] = useState("");
    const [update, setUpdate] = useState(0);
    const quickviewDetails = useMemo(() => {
      return UserStore.isHydrated && UserStore?.quickViewDetails
        ? { ...UserStore?.quickViewDetails }
        : {};
    }, []);

    const [transactionsData, setTransactionsData] = useState([]);
    const [taxData, setTaxData] = useState([]);
    const [vertualRows, setVertualRows] = useState([]);
    const [filteredColumn, setFilteredColumn] = useState("current_value");
    const [isFilteredColumnAscending, setIsFilteredColumnAscending] =
      useState(false);
    const [loading, setLoading] = useState(true);
    const [clientWidth, setClientWidth] = useState(300);
    const [sortingAnnoucement, setSortingAnnoucement] = useState("");

    const transactions = UserStore.instrumentDetails?.transaction;
    const taxLots = UserStore.instrumentDetails?.taxLots;
    const docClientWidth = document.documentElement.clientWidth;
    const instrumentId = document.getElementById(
      quickviewDetails?.instrumentList?.[0]?.id,
    );

    const instrumentNameCellRenderer = (_, data, key) => {
      const getFormattedInstrimentName = () => {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="info_tooptip_icon">
              <p
                style={{ visibility: "hidden", position: "absolute" }}
                id={data?.id}
              >
                {data.instrument_name}
              </p>

              <OverflowingText
                width={clientWidth - 25}
                text={data.instrument_name}
                id={`data-${data.id}`}
              />
            </div>
          </div>
        );
      };

      switch (key) {
        case "instrument_name":
          return (
            <div className="instrument_name_cell">
              {getFormattedInstrimentName()}
            </div>
          );
        case "as_on_date":
          return (
            <p className="instrument_name_cell showDate">
              {getDisplayDate(data.as_on_date) || "-"}
            </p>
          );
        case "qty":
          return (
            <p className="instrument_name_cell font-roboto">
              {Number(data.qty)?.toLocaleString("hi-IN")}
            </p>
          );
        case "current_value":
          return (
            <p className="instrument_name_cell font-roboto">
              {numWithUnit(data.dataCurrValue, data.dataCurreValueUnit)}
            </p>
          );
        case "inv_value":
          return (
            <p className="instrument_name_cell font-roboto">
              {numWithUnit(data.dataInvValue, data.dataInvUnit)}
            </p>
          );
        case "unrealised_gain":
          return <UnrealisedGainLossRenderer data={data} />;
        case "realised_Gains":
          return (
            <p className="instrument_name_cell font-roboto">
              {numWithUnit(data.dataReal, data.dataRealUnit)}
            </p>
          );
        case "irr":
          return (
            <p className="instrument_name_cell font-roboto">
              {Number(data.irr) < 0 ? (
                <span className="negative">{getLimitPer(data.irr)}%</span>
              ) : (
                <span>{getLimitPer(data.irr)}%</span>
              )}
            </p>
          );
        default:
          return <p className="instrument_name_cell">{data.realised_Gains}</p>;
      }
    };

    const btnCellRenderer = (_, data) => {
      const _instrument =
        quickviewDetails?.quickViewDetails?.lstInstrumentReturns?.find(
          (item) =>
            data.measure_instr_id === item.instrument.measureinstrumentid,
        );

      return (
        <button
          onClick={() => {
            UserStore.setNormalHybirdFlow(false);

            UserStore.setSelectedInstrument({
              ..._instrument,
              instrument_name: _instrument.instrument.name,
              isUnitBased: _instrument.instrument.isUnitBased,
            });
            setOpenModal(true);
            PerformanceStore.setCurrentLoader("modalLoader");
            PerformanceStore.setPerformanceLoader("modalLoader", true);
            UserStore.getInstrumentData({
              instrumentId: _instrument.instrument.id,
              measureinstrumentid: _instrument.instrument.measureinstrumentid,
            });
          }}
          className="accordion_right_btn portfolio_button_animation buttonBlueBorder"
          aria-label="View Instrument Details"
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <svg
              width="8"
              height="12"
              viewBox="0 0 8 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.93394 5.64683L5.28634 6L4.93394 6.35317L0.706721 10.5896L1.41 11.2929L6.70289 6L1.41 0.707108L0.706722 1.41039L4.93394 5.64683Z"
                fill="white"
                stroke="#404040"
              />
            </svg>
          </span>
        </button>
      );
    };

    const columns = [
      {
        label: "Instrument Name",
        key: "instrument_name",
        sortable: true,
        cellRenderer: instrumentNameCellRenderer,
      },
      {
        label: "As on Date",
        key: "as_on_date",
        sortable: true,
        cellRenderer: instrumentNameCellRenderer,
      },
      {
        label: "Qty",
        key: "qty",
        sortable: true,
        cellRenderer: instrumentNameCellRenderer,
      },
      {
        label: "Inv Value",
        key: "inv_value",
        sortable: true,
        cellRenderer: instrumentNameCellRenderer,
      },
      {
        label: "Current Value",
        key: "current_value",
        sortable: true,
        cellRenderer: instrumentNameCellRenderer,
      },

      {
        label: "Unrealised Gains (%)",
        label2: (
          <>
            Unrealised Gains
            <br /> <p style={{ textAlign: "end" }}>(%)</p>
          </>
        ),
        key: "unrealised_gain",
        sortable: true,
        cellRenderer: instrumentNameCellRenderer,
        isMultipleSort: true,
        position:
          document.documentElement.clientWidth < 1200
            ? { top: "45px", left: "74px" }
            : { top: "37px", left: "80px" },
        forArrowCheck: "unrealised_gainsubtext",
        multiSortBox: [
          {
            key: "unrealised_gain",
            show: "Unrealised Gains",
          },
          {
            key: "subtext",
            show: "Unrealised Gains (%)",
          },
        ],
        headerLabel: "Sort By Unrealised Gains With %",
      },
      {
        label: "Realised Gain",
        label2: (
          <>
            Realised
            <br /> <p style={{ textAlign: "end" }}>Gain</p>
          </>
        ),
        key: "realised_Gains",
        sortable: true,
        cellRenderer: instrumentNameCellRenderer,
      },
      {
        label: "IRR ",
        key: "irr",
        sortable: true,
        cellRenderer: instrumentNameCellRenderer,
      },
      {
        label: "Action",
        key: "btn",
        cellRenderer: btnCellRenderer,
      },
    ];

    const handleSort = (key) => {
      setLastTableAction("sort");
      setIsFilteredColumnAscending((prevState) =>
        key === filteredColumn ? !prevState : false,
      );
      setFilteredColumn(key);
    };
    useEffect(() => {
      setUpdate((prev) => prev + 1);
    }, []);

    useEffect(() => {
      if (
        document.documentElement.clientWidth > 990 &&
        document.documentElement.clientWidth < 1200 &&
        update
      )
        setClientWidth(250);
      else if (
        document.documentElement.clientWidth < 1400 &&
        document.documentElement.clientWidth > 1200 &&
        update
      )
        setClientWidth(330);
      else if (
        document.documentElement.clientWidth < 1600 &&
        document.documentElement.clientWidth > 1400 &&
        update
      )
        setClientWidth(350);
      else if (
        document.documentElement.clientWidth > 1600 &&
        document.documentElement.clientWidth < 1980 &&
        update
      )
        setClientWidth(350);
      else if (document.documentElement.clientWidth > 1980 && update)
        setClientWidth(260);
      // eslint-disable-next-line
    }, [
      update,
      // document.documentElement.clientWidth,
      // document.getElementById(quickviewDetails?.instrumentList?.[0]?.id),
      docClientWidth,
      instrumentId,
      quickviewDetails,
    ]);

    useEffect(() => {
      const localData = UserStore.instrumentDetails?.transaction
        ? UserStore.instrumentDetails?.transaction
        : [];
      setTransactionsData([...localData]);
    }, [transactions]);

    useEffect(() => {
      const localData = UserStore.instrumentDetails?.taxLots
        ? UserStore.instrumentDetails?.taxLots
        : [];
      setTaxData([...localData]);
    }, [taxLots]);

    useEffect(() => {
      const mainData = UserStore?.quickViewDetails?.instrumentList;

      let rowData = [];
      rowData = mainData?.filter((data) => {
        if (!search) return true;
        else
          return data?.instrument_name
            ?.toLowerCase()
            ?.includes(search?.toLowerCase());
      });

      if (rowData) {
        if (
          (filteredColumn === "qty" ||
            filteredColumn === "inv_value" ||
            filteredColumn === "realised_Gains" ||
            filteredColumn === "irr") &&
          isFilteredColumnAscending
        ) {
          setVertualRows(
            [...rowData]?.sort(
              (a, b) =>
                Number(a?.[filteredColumn]) - Number(b?.[filteredColumn]),
            ),
          );
        }
        if (
          (filteredColumn === "qty" ||
            filteredColumn === "inv_value" ||
            filteredColumn === "realised_Gains" ||
            filteredColumn === "irr") &&
          !isFilteredColumnAscending
        ) {
          setVertualRows(
            [...rowData]
              ?.sort(
                (a, b) =>
                  Number(a?.[filteredColumn]) - Number(b?.[filteredColumn]),
              )
              .reverse(),
          );
        }

        if (
          (filteredColumn === "current_value" ||
            filteredColumn === "unrealised_gain") &&
          isFilteredColumnAscending
        ) {
          setVertualRows(
            [...rowData]?.sort(
              (a, b) =>
                Number(a?.[filteredColumn].text) -
                Number(b?.[filteredColumn].text),
            ),
          );
        }
        if (
          (filteredColumn === "current_value" ||
            filteredColumn === "unrealised_gain") &&
          !isFilteredColumnAscending
        ) {
          setVertualRows(
            [...rowData]
              ?.sort(
                (a, b) =>
                  Number(a?.[filteredColumn].text) -
                  Number(b?.[filteredColumn].text),
              )
              .reverse(),
          );
        }

        if (filteredColumn === "subtext" && isFilteredColumnAscending) {
          setVertualRows(
            [...rowData]?.sort(
              (a, b) =>
                Number(a?.unrealised_gain?.subtext) -
                Number(b?.unrealised_gain?.subtext),
            ),
          );
        }
        if (filteredColumn === "subtext" && !isFilteredColumnAscending) {
          setVertualRows(
            [...rowData]
              ?.sort(
                (a, b) =>
                  Number(a?.unrealised_gain?.subtext) -
                  Number(b?.unrealised_gain?.subtext),
              )
              .reverse(),
          );
        }

        if (
          filteredColumn === "instrument_name" &&
          !isFilteredColumnAscending
        ) {
          setVertualRows(
            [...rowData]?.sort((a, b) =>
              b?.[filteredColumn].localeCompare(a?.[filteredColumn]),
            ),
          );
        }
        if (filteredColumn === "instrument_name" && isFilteredColumnAscending) {
          setVertualRows(
            [...rowData]
              ?.sort((a, b) =>
                b?.[filteredColumn].localeCompare(a?.[filteredColumn]),
              )
              .reverse(),
          );
        }

        if (filteredColumn === "as_on_date" && isFilteredColumnAscending) {
          setVertualRows(
            [...rowData]?.sort((a, b) => {
              let aa = a?.[filteredColumn]?.split("-")?.join("");

              let bb = b?.[filteredColumn]?.split("-")?.join("");
              return aa < bb ? -1 : aa > bb ? 1 : 0;
            }),
          );
        }
        if (filteredColumn === "as_on_date" && !isFilteredColumnAscending) {
          setVertualRows(
            [...rowData]
              ?.sort((a, b) => {
                let aa = a?.[filteredColumn]?.split("-")?.join("");

                let bb = b?.[filteredColumn]?.split("-")?.join("");
                return aa < bb ? -1 : aa > bb ? 1 : 0;
              })
              .reverse(),
          );
        }
      }
      if (lastTableAction === "search") {
        setSearchMessage(
          rowData.length
            ? `${rowData.length} results found for ${
                search || "Empty search input"
              }`
            : `${noSearchResult} ${search}`,
        );
      }
    }, [filteredColumn, isFilteredColumnAscending, search]);

    useEffect(() => {
      setLoading(false);
    }, []);

    return (
      <>
        <div className="quick_details_container">
          <div className="quick_details_head">
            <div className="quic_details_title">
              <img src={dispImg[dataId]} alt="" />
              <h1 id="main-heading" tabIndex={-1}>
                {quickviewDetails?.instrumentList?.length} Instruments in{" "}
                <span>{dispTitle[dataId]}</span>
              </h1>
            </div>
            {/* <h2 className="portfolio_breadcr_title"></h2> */}
          </div>
          <div className="quick_details_value_wrapper">
            <h2 className="quick_details_value blueTextColor">
              Total Current Value{" "}
              <span>
                {commaSeperated(
                  quickviewDetails?.quickViewDetails?.total_current_value.value,
                )}{" "}
                <span
                  style={{ fontFamily: "Encode Sans" }}
                  className="unitFont"
                >
                  {
                    quickviewDetails?.quickViewDetails?.total_current_value
                      .value_type
                  }{" "}
                </span>
              </span>
            </h2>
            <div className="filter_search_btn">
              <SearchButton
                search={search}
                setSearch={(data) => {
                  setLastTableAction("search");
                  setSearch(data);
                }}
                width={"423px"}
                ariaLabel={"Search By Instrument Name"}
              />
            </div>
            <SearchResult searchMessage={searchMessage} />
          </div>
          <div
            style={{
              backgroundColor: "#fff",
              height: 12,
              width: "100%",
              position: "sticky",
              top: 0,
            }}
          />
          {quickviewDetails?.instrumentList &&
          quickviewDetails?.instrumentList?.length ? (
            <Table
              checkData={UserStore?.quickViewDetails.instrumentList}
              data={vertualRows}
              columns={columns}
              tableClass="equity_summary_table"
              EmptyState={
                <div
                  className="no_data_liabilites"
                  style={{
                    width: "100%",
                    height: "100%",
                    margin: "0",
                    borderRadius: "0",
                    backgroundColor: loading ? "#FFF" : "#f6f7fa",
                  }}
                >
                  <>
                    {!loading && (
                      <EmptyLiabilities
                        type={"quickviews"}
                        message={"No Quick Views Found"}
                      />
                    )}
                  </>
                </div>
              }
              handleSort={(key) => handleSort(key)}
              sortKey={filteredColumn}
              isAscending={isFilteredColumnAscending}
              setSortingAnnoucement={setSortingAnnoucement}
            />
          ) : (
            <EmptyLiabilities
              type={"quickviews"}
              message={"No quickviews found"}
            />
          )}
        </div>
        <div
          id="aria-announcer"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {sortingAnnoucement}
        </div>

        <Modal
          mdlStyle={{ backgroundColor: "rgba(0, 0, 0, 0.75)" }}
          modalStyle={{
            height:
              !UserStore.isHybridNormalInstrument !== "1" ? "510px" : "490px",
          }}
          onClose={() => setOpenModal(false)}
          open={openModal}
          width={
            document.documentElement.clientWidth > 1200 ? "1200px" : "990px"
          }
          minWidth={"890px"}
          modalContainerClass="snapshot_modal_container quickviewModal"
          //dialogId={`instrument-${UserStore.instrumentDetails?.details?.instrument_name}-details`}
          dialogId="instrument-details"
          announcement={`instrument-details`}
        >
          {openModal && (
            <TransactionModal
              open={openModal}
              transactions={transactionsData}
              taxData={taxData}
              loaderCondition={PerformanceStore.modalLoader}
            />
          )}
        </Modal>
      </>
    );
  },
);

export default QuickDetailsHead;
